import { observer } from 'mobx-react';
import useAiContext from 'app/util/hooks/useAiContext';
import { formatRunoutDate } from './CapacityUtils';

const formatInterfaceList = (interfaces) =>
  interfaces
    .map((iface) => {
      const capacity = parseInt(iface.get('snmp_speed')) * 1000000;
      const util = iface.get('util');
      const runout = iface.get('runout');
      const inUtil = util.in?.current.pct || 'N/A';
      const outUtil = util.out?.current.pct || 'N/A';
      const inRunout = formatRunoutDate(runout?.in?.date);
      const outRunout = formatRunoutDate(runout?.out?.date);

      return `
- ${iface.get('interface_description')} (${iface.get('device_name')})
  Severity: ${iface.severity}
  Utilization: In ${inUtil}%, Out ${outUtil}%
  Capacity: ${capacity ? (capacity / 1e9).toFixed(2) : 'N/A'} Gbps
  Runout: In ${inRunout}, Out ${outRunout}`;
    })
    .join('\n');

const getHighestUtil = (iface) => {
  const util = iface.get('util') || {};
  const inPct = Number(util.in?.current.pct) || 0;
  const outPct = Number(util.out?.current.pct) || 0;
  return Math.max(inPct, outPct);
};

export const CapacityAiDetailsProvider = observer(({ plan, children }) => {
  const models = plan.interfaceGroup?.interfaceCollection?.models || [];

  const { unhealthyInterfaces, healthyInterfaces } = models.reduce(
    (acc, iface) => {
      if (iface.severity >= 1) {
        acc.unhealthyInterfaces.push(iface);
      } else {
        acc.healthyInterfaces.push(iface);
      }
      return acc;
    },
    { unhealthyInterfaces: [], healthyInterfaces: [] }
  );

  const sortedHealthyInterfaces = healthyInterfaces
    .slice()
    .sort((a, b) => getHighestUtil(b) - getHighestUtil(a))
    .slice(0, 1000);

  const thresholds = plan.get('thresholds') || { utilization: {} };

  const buildContext = () => {
    if (!plan) {
      return '';
    }

    return `
User is on the details page of the capacity planning page for capacity plan "${plan.get('name')}". 
Healthy Interfaces are not loaded into your context
Description: ${plan.get('description')}
Severity: ${plan.get('severity')}
Total Interfaces: ${models.length}
Warning Threshold: ${thresholds.utilization?.warning}%
Critical Threshold: ${thresholds.utilization?.critical}%
Runout Strategy: ${plan.get('runoutStrategy')}

Warning and Critical Interfaces:
${formatInterfaceList(unhealthyInterfaces) || 'None'}

First 1000 Healthy Interfaces:
${formatInterfaceList(sortedHealthyInterfaces) || 'None'}`.trim();
  };

  const context = buildContext();

  const suggestions = [
    'Summarize capacity plan status',
    unhealthyInterfaces.length > 0
      ? 'How can I prevent this plan from running out of capacity?'
      : 'Show me interfaces that need attention',
    unhealthyInterfaces.length > 0
      ? 'What is the impact of running out of capacity?'
      : 'Which healthy interfaces are closest to running out of capacity?',
    'Recommend threshold adjustments'
  ];

  useAiContext({
    type: 'capacityPlan',
    name: `Capacity Plan: ${plan?.get('name')}`,
    context,
    model: plan,
    suggestions
  });

  return children || null;
});
